import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Loader } from "@/components/ui-helpers";
import { Download, UserSearch } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const PRESETS = {
  this_week: () => { const t = new Date(); const s = new Date(t); s.setDate(t.getDate() - ((t.getDay() + 6) % 7)); return [iso(s), iso(t)]; },
  last_week: () => { const t = new Date(); const s = new Date(t); s.setDate(t.getDate() - ((t.getDay() + 6) % 7) - 7); const e = new Date(s); e.setDate(s.getDate() + 6); return [iso(s), iso(e)]; },
  this_month: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)]; },
  last_month: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), iso(new Date(t.getFullYear(), t.getMonth(), 0))]; },
};
const PRESET_LABELS = [["this_week", "This week"], ["last_week", "Last week"], ["this_month", "This month"], ["last_month", "Last month"], ["custom", "Custom"]];
const METRICS = [["total_actions", "Total actions"], ["cases_worked", "Cases worked"], ["tasks_completed", "Tasks completed"], ["notes_added", "Notes added"],
  ["messages_sent", "Messages sent"], ["follow_ups", "Follow-ups"], ["stage_changes", "Stage changes"], ["documents", "Documents"], ["clients_touched", "Clients touched"], ["logins", "Logins"]];

const actionLabel = (a) => ({ task_completed: "Task completed", team_messages: "Team messages", application_stage_change: "Case stage changed", client_note_add: "Note added",
  message_send: "Client message sent", client_interaction: "Client interaction logged", lead_status_change: "Lead status changed" }[a]
  || (a || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()));
const targetLink = (t) => {
  const [kind, id] = (t || "").split(":");
  if (!id) return null;
  return { application: `/admin/applications/${id}`, client: `/admin/clients/${id}`, lead: `/admin/leads?lead=${id}` }[kind] || null;
};

const downloadCsv = (name, header, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const blob = new Blob([[header, ...rows].map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href);
};

export function StaffActivityReport() {
  const [preset, setPreset] = useState("this_month");
  const [[frm, to], setRange] = useState(PRESETS.this_month());
  const [who, setWho] = useState("");
  const [staff, setStaff] = useState([]);
  const [data, setData] = useState(null);
  const [shown, setShown] = useState(100);

  useEffect(() => { api.get("/audit-logs/users").then((r) => setStaff(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!frm || !to || frm > to) return;
    setData(null); setShown(100);
    api.get("/reports/staff-activity", { params: { frm, to, user_id: who || undefined } }).then((r) => setData(r.data)).catch(() => setData({ rows: [] }));
  }, [frm, to, who]);
  const pick = (k) => { setPreset(k); if (k !== "custom") setRange(PRESETS[k]()); };
  const max = useMemo(() => Math.max(1, ...((data?.rows || []).map((r) => r.total_actions))), [data]);

  const exportCsv = () => {
    const period = `${frm}_to_${to}`;
    if (data.rows) downloadCsv(`staff-activity_${period}.csv`, ["Staff", ...METRICS.map(([, l]) => l)], data.rows.map((r) => [r.name, ...METRICS.map(([k]) => r[k])]));
    else downloadCsv(`staff-activity_${data.person.name}_${period}.csv`, ["Date & time", "Action", "Details", "Item"], data.items.map((i) => [(i.at || "").replace("T", " ").slice(0, 16), actionLabel(i.action), i.detail, i.target]));
  };

  return (
    <Card className="p-6 mt-6" data-testid="staff-activity-report">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><UserSearch className="h-4 w-4 text-primary" /><h3 className="font-head font-bold text-slate-900">Staff activity report</h3></div>
        <button data-testid="staff-report-download" onClick={exportCsv} disabled={!data || (!data.rows && !data.items)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:border-slate-300 disabled:opacity-40"><Download className="h-3.5 w-3.5" /> Download CSV</button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="inline-flex flex-wrap bg-slate-100 rounded-xl p-1" data-testid="staff-report-range">
          {PRESET_LABELS.map(([k, l]) => (
            <button key={k} data-testid={`staff-report-range-${k}`} onClick={() => pick(k)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${preset === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <input type="date" data-testid="staff-report-from" value={frm} max={to} onChange={(e) => setRange([e.target.value, to])} className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm" />
            to <input type="date" data-testid="staff-report-to" value={to} min={frm} onChange={(e) => setRange([frm, e.target.value])} className="px-2 py-1.5 rounded-lg border border-slate-200 text-sm" />
          </div>
        )}
        <select data-testid="staff-report-person" value={who} onChange={(e) => setWho(e.target.value)} className="sm:ml-auto px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm">
          <option value="">All staff (compare)</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div className="text-xs text-slate-400 mb-3" data-testid="staff-report-period">{frm} → {to}</div>

      {!data ? <Loader /> : data.rows ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" data-testid="staff-report-compare">
            <thead className="text-xs uppercase tracking-wider text-slate-500"><tr className="border-b border-slate-100">
              <th className="text-left py-2 font-semibold">Staff</th>{METRICS.map(([k, l]) => <th key={k} className="text-right py-2 px-1.5 font-semibold text-[10px] leading-tight align-bottom">{l}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {data.rows.map((r) => (
                <tr key={r.id} data-testid={`staff-report-row-${r.id}`}>
                  <td className="py-2.5 pr-3"><button onClick={() => setWho(r.id)} className="font-medium text-slate-800 hover:text-primary hover:underline">{r.name}</button>
                    <div className="h-1.5 mt-1 rounded-full bg-slate-100 w-32"><div className="h-full rounded-full bg-primary" style={{ width: `${(r.total_actions / max) * 100}%` }} /></div></td>
                  {METRICS.map(([k]) => <td key={k} className={`py-2.5 px-2 text-right tabular-nums ${k === "total_actions" ? "font-bold text-slate-900" : "text-slate-600"}`}>{r[k]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-400 mt-3">Click a name to see everything that person did in this period.</p>
        </div>
      ) : (
        <div data-testid="staff-report-person-view">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
            {METRICS.map(([k, l]) => (
              <div key={k} className="rounded-xl border border-slate-100 p-3" data-testid={`staff-report-metric-${k}`}>
                <div className="text-xl font-head font-extrabold text-slate-900">{data.metrics[k]}</div><div className="text-[11px] text-slate-500">{l}</div>
              </div>
            ))}
          </div>
          {data.items.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">No activity in this period.</div> : (
            <div className="rounded-xl border border-slate-100 divide-y divide-slate-50" data-testid="staff-report-items">
              {data.items.slice(0, shown).map((i, n) => {
                const to2 = targetLink(i.target);
                return (
                  <div key={n} className="grid grid-cols-[1fr_auto] sm:grid-cols-[7rem_11rem_1fr_auto] gap-x-3 gap-y-0.5 items-start px-3 py-2 text-sm">
                    <span className="text-xs text-slate-400 tabular-nums sm:order-none">{(i.at || "").replace("T", " ").slice(0, 16)}</span>
                    <span className="font-medium text-slate-700 truncate col-span-2 sm:col-span-1 order-3 sm:order-none">{actionLabel(i.action)}</span>
                    <span className="min-w-0 text-slate-500 truncate col-span-2 sm:col-span-1 order-4 sm:order-none" title={i.detail || ""}>{i.detail || "—"}</span>
                    <span className="order-2 sm:order-none text-right">{to2 && <Link to={to2} className="text-xs font-semibold text-primary">Open</Link>}</span>
                  </div>
                );
              })}
            </div>
          )}
          {data.items.length > shown && <button data-testid="staff-report-more" onClick={() => setShown(shown + 100)} className="mt-3 px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600">Show more ({data.items.length - shown} left)</button>}
        </div>
      )}
    </Card>
  );
}
